import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { isMainGameRoute, routes } from '../../data/routes';
import { observer } from 'mobx-react-lite';
import { undoableSwitchMode } from '../../stores/undo/operation/SwitchModeOp';
import { UiConstants } from "../../data/UiConstants";
import { localSettingsStore } from '../../stores/LocalSettingsStore';
import { GiHamburgerMenu } from 'react-icons/gi';
import { BiArrowBack, BiPlayCircle } from 'react-icons/bi';
import { sharedStore } from '../../stores/SharedStore';
import { editorStore } from '../../stores/EditorStore';
import { userStore } from '../../stores/UserStore';
import { navigateTo } from '../../helper/navigationHelpers';
import { AiOutlineDatabase } from 'react-icons/ai';
import { SettingsMenu } from './SettingsMenu';
import { logOut } from '../auth/LogoutButton';
import { gameStore } from '../../stores/GameStore';
const debugServerColor = process.env.DEBUG_SERVER_COLOR;
const NavLinkContainer = styled.a.withConfig({
  displayName: "Header__NavLinkContainer",
  componentId: "sc-rrkkmr-0"
})(["margin:2px;padding-top:2px;padding-bottom:2px;padding-left:6px;padding-right:6px;border-radius:", ";background-color:", ";color:white;&:hover{cursor:pointer;background-color:", ";}&.selected{padding-top:0px;padding-bottom:0px;background-color:", ";border:2px solid black;}text-decoration:none;"], UiConstants.BORDER_RADIUS, UiConstants.COLOR_DARK_BUTTON, UiConstants.COLOR_DARK_BUTTON_HOVER, UiConstants.COLOR_SELECTION_HIGHLIGHT);
const DropdownMenuContent = styled.div.withConfig({
  displayName: "Header__DropdownMenuContent",
  componentId: "sc-rrkkmr-1"
})(["display:none;position:absolute;background-color:#f9f9f9;min-width:160px;box-shadow:0px 8px 16px 0px rgba(0,0,0,0.2);padding:12px 16px;top:0px;right:0px;border-radius:", ";"], UiConstants.BORDER_RADIUS);
const DropdownMenuContainer = styled.div.withConfig({
  displayName: "Header__DropdownMenuContainer",
  componentId: "sc-rrkkmr-2"
})(["position:relative;display:inline-block;padding-left:7px;&:hover ", "{display:block;}"], DropdownMenuContent);
export const UndoableNavLink = ({
  to,
  hideWhenSelected,
  children
}) => {
  const history = useHistory();
  const location = useLocation();
  const isSelected = location.pathname === to;
  if (hideWhenSelected && isSelected) return null;
  const className = isSelected ? "selected" : "";
  return /*#__PURE__*/React.createElement(NavLinkContainer, {
    href: "#",
    className: className,
    onClick: e => {
      e.preventDefault();
      undoableSwitchMode(to, history);
    },
    draggable: false
  }, children);
};
const BackToManagementViewLink = observer(() => {
  const history = useHistory();
  if (!userStore.mayUseWorkshopManagementView || isMainGameRoute()) return null;
  return /*#__PURE__*/React.createElement(NavLinkContainer, {
    href: "#",
    onClick: e => {
      e.preventDefault();
      gameStore.setDebugStartMarker(null, null);
      gameStore.setDebugStartNodeModelId(null);
      navigateTo(history, routes.workshopManagement);
    },
    draggable: false
  }, /*#__PURE__*/React.createElement(AiOutlineDatabase, {
    style: {
      position: "relative",
      top: 2
    }
  }));
});
const HeaderContainer = styled.div.withConfig({
  displayName: "Header__HeaderContainer",
  componentId: "sc-rrkkmr-3"
})(["position:fixed;top:0;padding:3px;padding-top:9px;height:36px;box-shadow:0 0 0.5em 0 #5555557f;border-top:none;background-color:", ";z-index:", ";overflow:unset;"], debugServerColor ? debugServerColor : "white", UiConstants.Z_INDEX_HEADER);
const borderRadius = "8px";
const HeaderLeftContainer = styled(HeaderContainer).withConfig({
  displayName: "Header__HeaderLeftContainer",
  componentId: "sc-rrkkmr-4"
})(["left:0;border-left:none;border-radius:0 0 ", " 0;"], borderRadius);
const HeaderRightContainer = styled(HeaderContainer).withConfig({
  displayName: "Header__HeaderRightContainer",
  componentId: "sc-rrkkmr-5"
})(["right:0;border-right:none;border-radius:0 0 0 ", ";padding-top:7px;"], borderRadius);
export const Header = observer(() => {
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(HeaderLeftContainer, null, /*#__PURE__*/React.createElement(HeaderLeft, null)), editorStore.isConnectedAndReady && /*#__PURE__*/React.createElement(HeaderRightContainer, null, /*#__PURE__*/React.createElement(HeaderRight, null), /*#__PURE__*/React.createElement(SettingsMenu, {
    showNonGameSettings: true
  })));
});
const BackIcon = styled(BiArrowBack).withConfig({
  displayName: "Header__BackIcon",
  componentId: "sc-rrkkmr-6"
})(["position:absolute;top:9px;"]);
const PlayIcon = styled(BiPlayCircle).withConfig({
  displayName: "Header__PlayIcon",
  componentId: "sc-rrkkmr-7"
})(["position:absolute;top:9px;"]);
const TextAfterIcon = styled.span.withConfig({
  displayName: "Header__TextAfterIcon",
  componentId: "sc-rrkkmr-8"
})(["margin-left:14px;"]);
const HeaderLeft = observer(() => {
  const {
    t
  } = useTranslation();
  const location = useLocation();

  if (location.pathname == routes.editorGame) {
    return /*#__PURE__*/React.createElement(UndoableNavLink, {
      to: sharedStore.previousHistoryLocationPathname
    }, /*#__PURE__*/React.createElement(BackIcon, null), /*#__PURE__*/React.createElement(TextAfterIcon, null));
  }

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(UndoableNavLink, {
    to: routes.editorGame
  }, /*#__PURE__*/React.createElement(PlayIcon, null), "\xA0", /*#__PURE__*/React.createElement(TextAfterIcon, null, t("shared.headline_game"))), /*#__PURE__*/React.createElement(UndoableNavLink, {
    to: routes.editorMap,
    hideWhenSelected: true
  }, t("shared.headline_editor")), /*#__PURE__*/React.createElement(UndoableNavLink, {
    to: routes.editorAction,
    hideWhenSelected: true
  }, t("shared.headline_action_editor")));
});
const HeaderRight = observer(() => {
  const {
    t
  } = useTranslation();
  const location = useLocation();
  const {
    isProductionEditor
  } = localSettingsStore;

  if (!isProductionEditor || !editorStore.isMainGameEditor) {
    if (location.pathname == routes.editorGame) {
      return null;
    } else {
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(BackToManagementViewLink, null));
    }
  }

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DropdownMenuContainer, null, /*#__PURE__*/React.createElement(GiHamburgerMenu, {
    style: {
      position: "relative",
      top: 2
    }
  }), /*#__PURE__*/React.createElement(DropdownMenuContent, null, /*#__PURE__*/React.createElement(UndoableNavLink, {
    to: routes.editorCombat
  }, t("shared.headline_combat_configurator")), /*#__PURE__*/React.createElement(UndoableNavLink, {
    to: routes.editorTileAssets
  }, t("shared.headline_tile_asset_editor")), /*#__PURE__*/React.createElement(UndoableNavLink, {
    to: routes.editorAnimations
  }, t("shared.headline_animations_editor")), /*#__PURE__*/React.createElement(UndoableNavLink, {
    to: routes.translationManagement
  }, t("shared.headline_translation_management")))), "\xA0", /*#__PURE__*/React.createElement(BackToManagementViewLink, null));
});
export const HeaderLogoutButton = () => {
  const {
    t
  } = useTranslation();
  return /*#__PURE__*/React.createElement(NavLinkContainer, {
    href: "#",
    onClick: logOut
  }, t("editor.auth_log_out"));
};
export const LoadingHeaderRightManagement = observer(() => {
  return /*#__PURE__*/React.createElement(HeaderRightContainer, null, /*#__PURE__*/React.createElement(HeaderLogoutButton, null));
});
export const LoadingHeaderRightEditor = observer(() => {
  return /*#__PURE__*/React.createElement(HeaderRightContainer, null, /*#__PURE__*/React.createElement(BackToManagementViewLink, null), /*#__PURE__*/React.createElement(HeaderLogoutButton, null));
});